import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { CommonModal } from 'wsm-common-modal';
import { usePrefs } from 'ws-scripts/modules/common';
import { getFilterIdByLabel, getSortIdByLabel, KBB_RESEARCHABLE_API } from '../constants';
import StarRating from './StarRating';
import ReviewHeader from './ReviewHeader';
import Pagination from './Pagination';
import { reviewsAPI } from '../api/reviewsService';
import { scrollModalToTop } from '../utilities/modalUtils';
import './AllReviewsModal.scss';

const AllReviewsModal = ({ reviews, onClose, isOpen }) => {
	// Get vehicleData from Redux store
	const vehicleData = useSelector((state) => state.widgetInfo?.vehicleData);

	const [sortBy, setSortBy] = useState(KBB_RESEARCHABLE_API.sort.MOST_RECENT.label);
	const [filterBy, setFilterBy] = useState(KBB_RESEARCHABLE_API.filter.ALL_RATINGS.label);

	// Pagination state
	const [paginatedReviews, setPaginatedReviews] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [totalReviews, setTotalReviews] = useState(0);

	const prefs = usePrefs();
	const {
		reviewHeadingWeight = 'font-weight-bold',
		reviewHeadingCase = 'text-capitalize',
		reviewRatingFontSize = 'ddc-font-size-large',
		useInverseTextColor = 'false',
		circleRatingBgColor = 'bg-primary',
		reviewsPerPage = '15' // Pref to control number of reviews per page (US1762682)
	} = prefs;

	const perPage = parseInt(reviewsPerPage, 10) || 15; // Parse pref value to integer, default to 15

	const textColor = useInverseTextColor === 'true' ? 'text-inverse' : '';
	const textMutedColor = useInverseTextColor === 'true' ? 'text-muted-inverse' : 'text-muted';

	// Build modal title from vehicle data
	const getModalTitle = useCallback(() => {
		if (vehicleData && vehicleData.year && vehicleData.make && vehicleData.model) {
			return `${vehicleData.year} ${vehicleData.make} ${vehicleData.model}`;
		}
		return 'Vehicle Reviews';
	}, [vehicleData]);

	// Fetch reviews when modal opens or dependencies change
	useEffect(() => {
		if (!isOpen) return;

		const fetchReviews = async () => {
			try {
				const sortValue = getSortIdByLabel(sortBy);
				const filterValue = getFilterIdByLabel(filterBy);

				// Service handles validation and throws errors on failure
				const data = await reviewsAPI.fetchReviews(vehicleData, currentPage, perPage, sortValue, filterValue);

				// Update state with fetched data
				setPaginatedReviews(data.results || []);
				setTotalPages(data.numPages || 0);
				setTotalReviews(data.totalReviews || 0);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('KBB Reviews - Failed to fetch:', error);
				setPaginatedReviews([]);
				setTotalPages(0);
				setTotalReviews(0);
			}
		};

		fetchReviews();
	}, [isOpen, currentPage, sortBy, filterBy, vehicleData, perPage]);

	// Handle page change
	const handlePageChange = useCallback((newPage) => {
		setCurrentPage(newPage);
		scrollModalToTop('.ws-kbb-reviews-modal', 100);
	}, []);

	const renderDropdown = useCallback((type, value, options, onChange) => (
		<div className="d-flex align-items-center px-4 py-3 border rounded dropdown-wrapper mt-3 mt-lg-0 mr-lg-3">
			<span className="dropdown-label font-weight-bold mr-2">{type === 'sort' ? 'Sort by:' : 'Rating:'}</span>
			<select
				className="form-control font-weight-normal h-auto border-0 p-0 pointer custom-dropdown"
				value={value}
				onChange={onChange}
			>
				{options.map((option) => (
					<option key={option.id} value={option.label}>
						{option.label}
					</option>
				))}
			</select>
		</div>
	), []);

	const sortOptions = Object.values(KBB_RESEARCHABLE_API.sort);
	const filterOptions = Object.values(KBB_RESEARCHABLE_API.filter);

	const handleSortChange = useCallback((e) => {
		setCurrentPage(1);
		setSortBy(e.target.value);
	}, []);

	const handleFilterChange = useCallback((e) => {
		setCurrentPage(1);
		setFilterBy(e.target.value);
	}, []);

	// Use paginated reviews if available, otherwise fall back to initial reviews
	const displayReviews = paginatedReviews.length > 0 ? paginatedReviews : reviews;

	const renderConsumerReview = useCallback((review, index) => {
		const isLastReview = index === displayReviews.length - 1;

		// Helper function to render individual rating rows
		const renderRatingRow = (label, value) => (
			<div className="d-table w-100 mb-3 ddc-font-size-medium">
				<div className="d-table-cell align-middle" style={{ width: '40%' }}>
					<span>{label}</span>
				</div>
				<div className="d-table-cell align-middle">
					<div className="d-flex align-items-center ml-5">
						<StarRating rating={parseFloat(value)} ratingScale={5} />
						<span className="ml-4 font-weight-bold">{parseFloat(value).toFixed(1)}</span>
					</div>
				</div>
			</div>
		);

		return (
			<div
				key={review.id || `review-${index}`}
				className={`d-flex flex-column ${!isLastReview ? 'border-bottom pb-4 mb-4' : ''}`}
			>
				{/* Star rating and title */}
				<ReviewHeader
					review={review}
					reviewHeadingWeight={reviewHeadingWeight}
					reviewHeadingCase={reviewHeadingCase}
					reviewRatingFontSize={reviewRatingFontSize}
					circleRatingBgColor={circleRatingBgColor}
					textColor={textColor}
					textMutedColor={textMutedColor}
				/>

				{/* Review content */}
				{review.pros && (
					<div className="mb-3 pb-3 mr-lg-390">
						<span className="font-weight-bold text-success mb-1">Pros </span>
						<span>{review.pros}</span>
					</div>
				)}

				{review.cons && (
					<div className="mb-21 mr-lg-390">
						<span className="font-weight-bold text-danger mb-1">Cons </span>
						<span>{review.cons}</span>
					</div>
				)}

				{review.text && (
					<div className="mb-21">
						{review.text}
					</div>
				)}
				{/* Individual Review Ratings */}
				<div className="kbb-review-ratings mb-21">
					<div className="row">
						<div className="col-12 col-md-6">
							{renderRatingRow('Value', review.value)}
							{renderRatingRow('Quality', review.quality)}
							{renderRatingRow('Reliability', review.reliability)}
						</div>
						<div className="col-12 col-md-6">
							{renderRatingRow('Performance', review.performance)}
							{renderRatingRow('Comfort', review.comfort)}
							{renderRatingRow('Styling', review.styling)}
						</div>
					</div>
				</div>

				{/* Recommendation indicator */}
				{review.recommendation === 5 && (
					<div className="d-flex align-items-center">
						<span className="text-success mr-2 font-weight-bold">
							✓
						</span>
						<span className="ddc-font-size-small">Recommends this Vehicle </span>
					</div>
				)}
			</div>
		);
	}, [
		reviewHeadingWeight,
		reviewHeadingCase,
		reviewRatingFontSize,
		circleRatingBgColor,
		textColor,
		textMutedColor,
		displayReviews.length
	]);

	// Calculate viewed results for display
	const viewedResults = currentPage * perPage;
	const displayViewedResults = Math.min(viewedResults, totalReviews);

	const modalTitle = (
		<div>
			<div className="ddc-font-size-large font-weight-bold">{getModalTitle()}</div>
			<div className="font-weight-normal ddc-font-size-small">Reviews from owners on KBB.com</div>
		</div>
	);

	return (
		<CommonModal
			title={modalTitle}
			isOpen={isOpen}
			onClose={onClose}
			width="responsive"
		>
			<div className="ws-kbb-reviews-modal">
				<div className="mb-4 pb-3">
					<div className="d-flex flex-column flex-lg-row justify-content-between">
						<div className="font-weight-bold text-nowrap text-left mb-3 mb-lg-0">
							{totalReviews || displayReviews.length} Reviews
						</div>
						<div className="d-flex flex-row flex-wrap">
							{renderDropdown('sort', sortBy, sortOptions, handleSortChange)}
							{renderDropdown('filter', filterBy, filterOptions, handleFilterChange)}
						</div>
					</div>
				</div>

				{displayReviews && displayReviews.length > 0 && (
					<>
						<div className="reviews-list">
							{displayReviews.map(renderConsumerReview)}
						</div>

						{/* Pagination section */}
						<div className="mt-4 pt-4 border-top">
							{/* "You have viewed X of Y results" text */}
							<div className="mb-3 text-center">
								<span className="text-muted">
									You have viewed {displayViewedResults} of {totalReviews} results
								</span>
							</div>

							{/* Pagination component - only show when more than 1 page */}
							{totalPages > 1 && (
								<div className="d-flex justify-content-center">
									<Pagination
										currentPage={currentPage}
										totalPages={totalPages}
										onPageChange={handlePageChange}
									/>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</CommonModal>
	);
};

AllReviewsModal.propTypes = {
	reviews: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		title: PropTypes.string,
		text: PropTypes.string,
		nickName: PropTypes.string,
		submissionDate: PropTypes.string,
		overallRating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		pros: PropTypes.string,
		cons: PropTypes.string,
		value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		reliability: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		performance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		styling: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		comfort: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		quality: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		recommendation: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	})),
	onClose: PropTypes.func.isRequired,
	isOpen: PropTypes.bool.isRequired
};

AllReviewsModal.defaultProps = {
	reviews: []
};

export default AllReviewsModal;
